// ===========================================================================
// Account + billing client (adult-only monetization). Talks to /api/account and
// /api/billing on the same origin. The account session token lives in
// localStorage; the chat flow passes it to /api/session so Plus perks apply.
// ASCII only.
// ===========================================================================

import { getKeepsake } from './keepsake.js';
import { solveLoginWork, LOGIN_WORK_TRIES } from './login-work.js';

const TOKEN_KEY = 'lumi.account.token';

export class AccountClient {
  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY) || null;
    this.account = null;
    this.roomPro = [];
  }

  get loggedIn() {
    return !!this.token;
  }

  /** The signed-in account's username, or null. */
  get username() {
    return (this.account && this.account.username) || null;
  }

  get isPlus() {
    return !!(this.account && this.account.plus);
  }

  /** Marked as Lumi staff by the server. Only ever a convenience here: the server decides. */
  get isStaff() {
    return !!(this.account && this.account.staff);
  }

  get settings() {
    return (this.account && this.account.settings) || {};
  }

  get cosmetics() {
    return (this.account && this.account.cosmetics) || [];
  }

  setToken(t) {
    this.token = t || null;
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  }

  _headers(json) {
    const h = {};
    if (json) h['Content-Type'] = 'application/json';
    if (this.token) h.Authorization = `Bearer ${this.token}`;
    return h;
  }

  async _post(path, body) {
    const r = await fetch(path, { method: 'POST', headers: this._headers(true), body: JSON.stringify(body || {}) });
    return { status: r.status, data: await r.json().catch(() => ({})) };
  }

  async _get(path) {
    const r = await fetch(path, { headers: this._headers(false) });
    return { status: r.status, data: await r.json().catch(() => ({})) };
  }

  /**
   * The signed-in device this token names (server devices.js), read off the
   * token's own payload: it is not secret, and nothing here trusts it beyond
   * telling a notice about this very device from one about another.
   */
  get sid() {
    try {
      const p = JSON.parse(atob(String(this.token || '').split('.')[0].replace(/-/g, '+').replace(/_/g, '/')));
      return typeof p.sid === 'string' ? p.sid : null;
    } catch {
      return null;
    }
  }

  /** Load the current account (if a token is present). */
  async refresh() {
    if (!this.token) {
      this.account = null;
      return null;
    }
    const sent = this.token;
    const { status, data } = await this._get('/api/account/me');
    if (status === 200) {
      // A token from before devices were kept comes back swapped for one
      // that names this device: kept, so this device shows in the list.
      if (data.token) this.setToken(data.token);
      this.account = data.account;
      this.roomPro = data.roomPro || [];
      return this.account;
    }
    // Only the token that was refused: one stored meanwhile (a sign-in that
    // landed while this was asked) is somebody's new sign-in.
    if (status === 401 && this.token === sent) {
      this.setToken(null);
      this.account = null;
    }
    return null;
  }

  /** Exchange a birth year for an age token (band proof) needed to register. */
  async ageToken(birthYear) {
    const r = await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ birthYear }),
    });
    const d = await r.json().catch(() => ({}));
    return { status: r.status, ageToken: d.ageToken, band: d.band, error: d.error };
  }

  async register(username, password, ageToken, fingerprint) {
    // The keepsake key goes with the proof: the server accepts a proof only
    // from the device it is bound to, so a copied one opens no account. The
    // fingerprint goes too: a proof held by no keepsake is held by the address
    // and the device together, and one nobody redeemed is redeemed by them here.
    const keepsake = getKeepsake()?.key;
    const { status, data } = await this._post('/api/account/register', { username, password, ageToken, keepsake, fingerprint });
    if (status === 200) {
      this.setToken(data.token);
      this.account = data.account;
    }
    return { status, data };
  }

  async login(username, password, fingerprint) {
    // The keepsake key goes along, and the session this browser holds rides in
    // the Authorization header: a device that has signed in to the account
    // before is known to the login limits by either, never by the fingerprint.
    const keepsake = getKeepsake()?.key;
    let { status, data } = await this._post('/api/account/login', { username, password, fingerprint, keepsake });
    // Past the username's sign-in budget the server asks a device it does not
    // know for work instead of a wait (login-work.js): the counter is found away
    // from the main thread and the same sign-in goes again with it. The form is
    // still waiting on this call, so its button stays disabled throughout. A
    // challenge that cannot be solved, or more of them than LOGIN_WORK_TRIES,
    // leaves the server's own answer for the form.
    for (let tries = 0; status === 429 && typeof data?.challenge === 'string' && tries < LOGIN_WORK_TRIES; tries += 1) {
      let counter;
      try {
        counter = await solveLoginWork(data.challenge, data.difficulty);
      } catch {
        break;
      }
      ({ status, data } = await this._post('/api/account/login', { username, password, fingerprint, keepsake, challenge: data.challenge, counter }));
    }
    if (status === 200) {
      this.setToken(data.token);
      this.account = data.account;
    }
    return { status, data };
  }

  /** First login for an admin-created (claimable) account: set the password. */
  async claim(username, password, ageToken, fingerprint) {
    const keepsake = getKeepsake()?.key; // as register: the proof counts only from its own device
    const { status, data } = await this._post('/api/account/claim', { username, password, ageToken, keepsake, fingerprint });
    if (status === 200) {
      this.setToken(data.token);
      this.account = data.account;
    }
    return { status, data };
  }

  /**
   * Set a new password using the code from signup.
   *
   * A 403 carries attemptsLeft, so the form can tell somebody how many tries
   * remain instead of letting them rattle the same door forever, and exhausted
   * once there is a person to ask instead.
   */
  async recover(username, code, password, fingerprint) {
    const { status, data } = await this._post('/api/account/recover', {
      username, code, password, fingerprint,
    });
    if (status === 200) {
      this.setToken(data.token);
      this.account = data.account;
    }
    return { status, data };
  }

  /** Lost the password AND the code: ask a human. Always answers ok. */
  async recoveryHelp(username, fingerprint) {
    const { status, data } = await this._post('/api/account/recovery-help', { username, fingerprint });
    return { status, data };
  }

  /** A one-shot notice for the client to celebrate (e.g. an admin-granted Plus), or null. */
  get notice() {
    return (this.account && this.account.notice) || null;
  }

  /** Clear the notice once its banner has been shown. */
  async ackNotice() {
    const { status, data } = await this._post('/api/account/notice', {});
    if (status === 200) this.account = data.account;
    return { status, data };
  }

  /** A sign-in from another device, until it is dismissed: { at, label }, or null. Never one about this device. */
  get deviceNotice() {
    const n = this.account && this.account.deviceNotice;
    return n && n.sid !== this.sid ? n : null;
  }

  async ackDeviceNotice() {
    const { status, data } = await this._post('/api/account/notice', { kind: 'device' });
    if (status === 200) this.account = data.account;
    return { status, data };
  }

  /** The devices signed in to the account, this one first, or null when it cannot say. */
  async devices() {
    const { status, data } = await this._get('/api/account/devices');
    return status === 200 && Array.isArray(data.devices) ? data.devices : null;
  }

  /** Sign one device out; this one, when it is this one. */
  async signOutDevice(id) {
    const out = await this._post('/api/account/devices/sign-out', { id });
    if (out.status === 200 && out.data.self) this.logout();
    return out;
  }

  /** Every device but this one. */
  async signOutOthers() {
    const out = await this._post('/api/account/devices/sign-out-others', {});
    if (out.status === 200 && out.data.token) this.setToken(out.data.token);
    return out;
  }

  /** A new password, with the current one; every other device is signed out. */
  async changePassword(current, password) {
    const out = await this._post('/api/account/password', { current, password });
    if (out.status === 200) {
      if (out.data.token) this.setToken(out.data.token);
      if (out.data.account) this.account = out.data.account;
    }
    return out;
  }

  logout() {
    this.setToken(null);
    this.account = null;
    this.roomPro = [];
  }

  /**
   * "Sign out": this device's session is ended on the server as well
   * (server devices.js), so it leaves the list of devices and its token is
   * nothing even to somebody who copied it; then it is forgotten here. A
   * server that cannot be reached still leaves this device signed out.
   */
  async signOut() {
    const sid = this.sid;
    if (sid) await this._post('/api/account/devices/sign-out', { id: sid }).catch(() => null);
    this.logout();
  }

  async billingMode() {
    const { data } = await this._get('/api/billing/mode');
    return data.mode;
  }

  async checkout(body) {
    return this._post('/api/billing/checkout', body);
  }

  async stubComplete(token) {
    const { status, data } = await this._post('/api/billing/stub-complete', { token });
    if (status === 200) {
      this.account = data.account;
      this.roomPro = data.roomPro || [];
    }
    return { status, data };
  }

  async saveSettings(patch) {
    const { status, data } = await this._post('/api/account/settings', patch);
    if (status === 200) this.account = data.account;
    return { status, data };
  }

  async roomSettings(code, patch) {
    const { status, data } = await this._post('/api/account/room-settings', { code, ...patch });
    if (status === 200) this.roomPro = (this.roomPro || []).map((r) => (data.room && r.code === data.room.code ? data.room : r));
    return { status, data };
  }

  /**
   * What may be paid with right now: the two switches, and the coins that
   * have both a price we trust and an address to give. Asked with the amount
   * so each coin can show what it would actually cost.
   */
  async payMethods(usdCents) {
    const { status, data } = await this._get(`/api/billing/methods?usdCents=${Number(usdCents) || 499}`);
    return status === 200 ? data : { card: false, crypto: false, coins: [] };
  }

  /** Open a crypto invoice. The price is the server's, never ours. */
  async cryptoInvoice(body) {
    const { status, data } = await this._post('/api/billing/crypto/invoice', body);
    return status === 200 ? { ok: true, invoice: data.invoice } : { ok: false, error: data.error || 'unavailable' };
  }

  /** What a front-page promotion would cost: the window and the price, no
   *  sign-in needed and nothing held. */
  async promoQuote(params) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    const { status, data } = await this._get(`/api/billing/promo/quote?${q}`);
    return status === 200 ? { ok: true, ...data } : { ok: false, error: data.error || 'unavailable' };
  }

  /** How is that invoice doing? Also returns the account, so a grant shows. */
  async cryptoStatus(id) {
    const { status, data } = await this._get(`/api/billing/crypto/invoice/${encodeURIComponent(id)}`);
    if (status !== 200) return { ok: false };
    if (data.account) this.account = data.account;
    return { ok: true, invoice: data.invoice, account: data.account };
  }

  /**
   * Run a purchase. In stub mode it completes in place (no redirect); with Stripe it
   * redirects to the hosted checkout. Returns { ok, mode } or { ok:false }.
   */
  async buy(body) {
    const { status, data } = await this.checkout(body);
    if (status !== 200 || data.error) return { ok: false, error: data.error || 'unavailable' };
    if (data.mode === 'stub') {
      const token = new URL(data.url, location.origin).searchParams.get('token');
      const res = await this.stubComplete(token);
      return { ok: res.status === 200, mode: 'stub' };
    }
    if (data.url) {
      window.location.href = data.url; // Stripe hosted checkout
      return { ok: true, mode: 'stripe', redirecting: true };
    }
    return { ok: false };
  }
}

export default AccountClient;
